import type {AnalyticsInstance, AnalyticsPlugin} from 'analytics';

const INTERACTIVE_SELECTOR =
  'a[href], button, input[type="button"], input[type="submit"], input[type="reset"], [role="button"]';

const MAX_TEXT_LENGTH = 255;

function truncate(s: string): string {
  return s.length > MAX_TEXT_LENGTH ? s.slice(0, MAX_TEXT_LENGTH) : s;
}

function getElementType(el: Element): 'button' | 'link' | 'submit' | 'reset' {
  // For <button> elements, use the explicit type attribute.
  // getAttribute('type') returns null for bare <button> (default type is "submit" in HTML,
  // but we treat unset as "button" so bare <button> does not report as a form submit).
  if (el instanceof HTMLButtonElement) {
    const explicitType = el.getAttribute('type');
    if (explicitType === 'submit') return 'submit';
    if (explicitType === 'reset') return 'reset';
    return 'button';
  }
  // For <input> elements, the type attribute is always explicit.
  if (el instanceof HTMLInputElement) {
    if (el.type === 'submit') return 'submit';
    if (el.type === 'reset') return 'reset';
    return 'button'; // input[type=button]
  }
  // [role="button"] on non-button elements (div, span, etc.)
  if (el.getAttribute('role') === 'button') return 'button';
  // a[href]
  return 'link';
}

// innerText is empty for detached elements in jsdom (no layout engine); fall back to textContent.
function getVisibleText(el: Element): string {
  return ((el as HTMLElement).innerText || el.textContent || '').trim();
}

function getElementName(el: Element): string | undefined {
  const dataName = el.getAttribute('data-element-name');
  if (dataName) return truncate(dataName);

  if (el instanceof HTMLInputElement) {
    return el.value ? truncate(el.value) : undefined;
  }

  const text = getVisibleText(el);
  if (text) return truncate(text);

  const ariaLabel = el.getAttribute('aria-label');
  return ariaLabel ? truncate(ariaLabel) : undefined;
}

function getText(el: Element): string {
  if (el instanceof HTMLInputElement) return truncate(el.value);
  return truncate(getVisibleText(el));
}

function getHref(el: Element): string | undefined {
  if (el instanceof HTMLAnchorElement) {
    return el.href || undefined;
  }
  const anchor = el.closest('a[href]') as HTMLAnchorElement | null;
  return anchor?.href || undefined;
}

export interface ClickProperties {
  element_type: 'button' | 'link' | 'submit' | 'reset';
  element_html_type: string;
  text: string;
  element_name?: string;
  element_id?: string;
  href?: string;
}

export function extractProperties(el: Element): ClickProperties {
  const elementName = getElementName(el);
  const href = getHref(el);

  return {
    element_type: getElementType(el),
    element_html_type: el.tagName.toLowerCase(),
    text: getText(el),
    ...(elementName !== undefined && {element_name: elementName}),
    ...(el.id && {element_id: el.id}),
    ...(href && {href}),
  };
}

export default function clickTracker(): AnalyticsPlugin {
  let attached = false;

  return {
    name: 'clickTracker',
    initialize: ({instance}: {instance: AnalyticsInstance}) => {
      if (typeof document === 'undefined') return;
      if (attached) return;
      attached = true;

      document.addEventListener(
        'click',
        (event: MouseEvent) => {
          const target = event.target as Element | null;
          if (!target) return;
          const el = target.closest(INTERACTIVE_SELECTOR);
          if (!el) return;
          void instance.track('element:clicked', extractProperties(el));
        },
        {capture: true},
      );
    },
  } as AnalyticsPlugin;
}
